// @ts-strict-ignore
import { cents, percentage } from '../../util/formatters'
import { EventParentTypes } from '../../config'
import React from 'react'
import type { AuditLog, AuditLogI, ValueFormatter } from './AuditLog'
import { auditTargetTypeLabel } from './audit-target-type'
import { Classes, Popover } from '../ui/popover'
type TargetModel =
    | Models.CampaignExtended
    | Models.AdGroupExtended
    | Models.KeywordExtended
    | Models.TargetExtended
    | Models.ProductAdExtended
    | Models.CampaignNegativeKeywordExtended
    | Models.NegativeKeywordExtended
    | Models.NegativeTargetExtended
    | Models.Recipe
    | Models.Promotion

export interface AuditLogItemI<TAuditLog extends AuditLog = AuditLog> {
    id: number
    audit_log?: TAuditLog
    audit_log_id: number
    target_id: number
    target_type_id: number
    meta: AuditLogItemMeta
    target: TargetModel
}

export type AuditLogItemMeta =
    | SmartBidAuditLogItem
    | PromotionAuditLogItem
    | BulkActionAuditLogItem
    | ProductAdCreateAuditLogItem
    | PublicApiProductCreateAuditLogItem
    | NegativeKeywordAuditLogItem
    | NegativeTargetExpressionAuditLogItem

interface SmartBidAuditLogItem {
    max_bid?: number
    min_bid?: number
    new_bid: number
    old_bid: number
    target_acos: number
    current_acos: number
    failure?: string
    change?: ClampedChange
}

interface PromotionAuditLogItem {
    rule_id: number
    keyword_id: number
    match_type: number
    keyword_text: string
    failure?: string
    change?: ClampedChange
}

interface ClampedChange {
    min_bid?: number
    max_bid?: number
    attempted: number
}

interface BulkActionAuditLogItem {
    previous_value_string?: string
    new_value_string?: string
    previous_value_int?: number
    new_value_int?: number
    asin?: string
    failure?: string
    change?: ClampedChange
    public_api_action?: string
}

interface ProductAdCreateAuditLogItem {
    asin: string
    reason?: string
    success: boolean
}

interface PublicApiProductCreateAuditLogItem {
    product_id: string
    success: boolean
}

interface NegativeKeywordAuditLogItem {
    type: string
    keyword: string
    match_type: number
}

interface NegativeTargetExpressionAuditLogItem {
    kind: string
    type: 'target'
    value: string
    expression_type?: number
}

export class AuditLogItem<TAuditLog extends AuditLog = AuditLog> implements AuditLogItemI<TAuditLog> {
    id: number
    meta: AuditLogItemMeta
    target_type_id: number
    audit_log: TAuditLog
    audit_log_id: number
    target_id: number
    target: TargetModel

    attribute(): string {
        return this.audit_log.getAttribute(
            this.audit_log.source_type_id === EventParentTypes.PublicApi &&
                'public_api_action' in this.meta
                ? this.meta.public_api_action
                : undefined,
        )
    }

    constructor(auditLog: AuditLogItemI<TAuditLog>) {
        Object.assign(this, auditLog)
    }

    targetType(): string | null {
        const targetType = auditTargetTypeLabel(this.target_type_id)
        if (targetType === null)
            console.warn('Cannot resolve target type for:', this.target_type_id)
        return targetType
    }

    event(): string | JSX.Element {
        const r = [this._event()]

        if ('change' in this.meta) {
            if ('min_bid' in this.meta.change) {
                r.push(
                    <div>
                        Tried to set bid to {cents(this.meta.change.attempted)} but min bid set to{' '}
                        {cents(this.meta.change.min_bid)}.
                    </div>,
                )
            } else if ('max_bid' in this.meta.change) {
                r.push(
                    <div>
                        Tried to set bid to {cents(this.meta.change.attempted)} but max bid set to{' '}
                        {cents(this.meta.change.max_bid)}.
                    </div>,
                )
            }
        }

        if ('failure' in this.meta) {
            const failureParts = this.meta.failure.split('|||')
            const failureMessage = failureParts.find((part) => part.trim()) ?? this.meta.failure
            r.push(<div>Failed due to Amazon error: {failureMessage}</div>)
        }
        if (r.length == 1 && typeof r[0] == 'string') {
            return <span dangerouslySetInnerHTML={{ __html: r[0] }} />
        }

        return <>{r}</>
    }

    _event(): string | JSX.Element {
        switch (this.audit_log.source_type_id) {
            case EventParentTypes.SmartBid:
                if ('new_bid' in this.meta) {
                    console.log('event is', this)

                    // If no bid change, bail
                    if (this.meta.new_bid == this.meta.old_bid) {
                        return <div>No bid change.</div>
                    }

                    const dir = this.meta.new_bid > this.meta.old_bid ? 'Increased' : 'Decreased'
                    const dir2 =
                        this.meta.current_acos < this.meta.target_acos ? 'increase' : 'decrease'
                    return (
                        <div>
                            {dir} bid from
                            {` ${cents(this.meta.old_bid)} to ${cents(this.meta.new_bid)}`}. Trying
                            to {dir2} ACoS from
                            {` ${percentage(this.meta.current_acos)} to target ${percentage(
                                this.meta.target_acos,
                            )}`}
                            {this.meta.new_bid == this.meta.max_bid && (
                                <>, but maximum bid was reached</>
                            )}
                            {this.meta.new_bid == this.meta.min_bid && (
                                <>, but minimum bid was reached</>
                            )}
                        </div>
                    )
                } else {
                    return 'Changed bids'
                }
            case EventParentTypes.Promotion:
            case EventParentTypes.BulkAction:
            case EventParentTypes.PublicApi:
            case EventParentTypes.Recipe:
                if ('new_value_string' in this.meta && 'previous_value_string' in this.meta) {
                    return `Changed ${this.attribute()} from "${
                        this.meta.previous_value_string
                    }" to "${this.meta.new_value_string}"`
                } else if ('new_value_int' in this.meta && 'previous_value_int' in this.meta) {
                    const attribute = this.attribute()

                    if (this.meta.new_value_int == this.meta.previous_value_int) {
                        return <div>No {attribute} change.</div>
                    }
                    const f = this._getFormatter(attribute)

                    if (
                        attribute === 'premium bid adjustment' ||
                        this.audit_log.isSmartBidToggle() ||
                        this.audit_log.isRecipeToggle() ||
                        this.audit_log.isPromotionToggle()
                    ) {
                        return `Changed ${this.attribute()} from "${f(
                            this.meta.previous_value_int,
                        )}" to "${f(this.meta.new_value_int)}"`
                    } else {
                        const dir =
                            this.meta.previous_value_int > this.meta.new_value_int ? (
                                <span className="text-red-500">Decreased</span>
                            ) : (
                                <span className="text-green-500">Increased</span>
                            )

                        return (
                            <>
                                {dir} {attribute} from {f(this.meta.previous_value_int)} to{' '}
                                {f(this.meta.new_value_int)}
                            </>
                        )
                    }
                } else if (
                    'previous_value_int' in this.meta &&
                    this.audit_log.isTargetAcosDelete()
                ) {
                    const attribute = this.attribute()
                    const f = this._getFormatter(attribute)
                    return `Deleted target acos of ${f(this.meta.previous_value_int)}`
                } else if (
                    'previous_value_int' in this.meta &&
                    'previous_value_string' in this.meta &&
                    (this.audit_log.isPortfolioDelete() ||
                        this.audit_log.isRecipeDelete() ||
                        this.audit_log.isPromotionDelete())
                ) {
                    const attribute = this.attribute()
                    return `Deleted ${attribute}`
                } else if (
                    'kind' in this.meta &&
                    'type' in this.meta &&
                    this.meta.type === 'target' &&
                    'value' in this.meta
                ) {
                    // Handle negative target expression creation events (new format)
                    const meta = this.meta as NegativeTargetExpressionAuditLogItem
                    const expressionLabel = this._resolveTargetExpressionLabel(
                        meta.kind,
                        meta.value,
                    )
                    return `Created negative target "${expressionLabel}"`
                } else if (
                    'keyword' in this.meta &&
                    'match_type' in this.meta &&
                    'type' in this.meta
                ) {
                    // Handle negative keyword creation events
                    if (this.meta.type === 'keyword') {
                        const matchTypeText =
                            this.meta.match_type === 1
                                ? 'exact match'
                                : this.meta.match_type === 2
                                  ? 'phrase match'
                                  : this.meta.match_type === 3
                                    ? 'broad match'
                                    : ''
                        return `Created ${matchTypeText} negative keyword "${this.meta.keyword}"`
                    } else if (this.meta.type === 'target') {
                        return `Created negative target "${this.meta.keyword}"`
                    }
                    return this.audit_log.event()
                } else if (this.audit_log.isCreateProductAd()) {
                    const meta = this.meta as ProductAdCreateAuditLogItem
                    if (meta.success) {
                        return (
                            <div>
                                <div>Create ad for {meta.asin}</div>
                                <div>Reason: {meta.reason}</div>
                            </div>
                        )
                    } else {
                        return (
                            <div>
                                <div>Failed to create ad for {meta.asin}</div>
                                <div>Reason: {meta.reason}</div>
                            </div>
                        )
                    }
                } else if (
                    this.audit_log.source_type_id === EventParentTypes.PublicApi &&
                    'event' in this.audit_log.meta &&
                    'mutationKind' in this.audit_log.meta.event &&
                    this.audit_log.meta.event.mutationKind === 'create'
                ) {
                    return this.audit_log.event()
                } else if (Array.isArray(this.meta)) {
                    let productIdentifier = 'asin'
                    if (this.meta[0]?.sku) {
                        productIdentifier = 'sku'
                    }

                    if (this.meta[0]?.[productIdentifier]) {
                        let result: JSX.Element
                        let success: JSX.Element
                        let failed: JSX.Element
                        if (Object.hasOwn(Object(this.meta[0]), 'success')) {
                            const successAsins = this.meta
                                .filter((item) => item?.success)
                                .map((item) => item?.[productIdentifier])
                            if (successAsins.length > 0) {
                                const firstTwentySuccessfulAsins = successAsins.slice(0, 20)
                                success = (
                                    <span>
                                        Created Product Ads with {productIdentifier}s:{' '}
                                        {firstTwentySuccessfulAsins.join(', ')}
                                        {successAsins.length > 20 && (
                                            <>
                                                &nbsp;and {(successAsins.length - 20).toString()}{' '}
                                                more
                                            </>
                                        )}
                                    </span>
                                )
                            }
                            const failedAsinsGroupByErrorReason = {}
                            const failedAsins = this.meta.filter((item) => !item?.success)
                            failedAsins.map((item) => {
                                failedAsinsGroupByErrorReason[item.reason] = []
                            })

                            if (failedAsins.length > 0) {
                                // we want to group the failed asins by error reason
                                failedAsins.forEach((failedItem) => {
                                    if (Object.hasOwn(Object(failedItem), 'reason')) {
                                        failedAsinsGroupByErrorReason[failedItem.reason].push(
                                            failedItem?.[productIdentifier],
                                        )
                                    }
                                })
                                const failedByGroup: JSX.Element[] = []
                                failed = (
                                    <>
                                        Failed to create Product Ads:
                                        <ul>
                                            {Object.keys(failedAsinsGroupByErrorReason).map(
                                                (reason) => {
                                                    let firstTwentyFailedAsins =
                                                        failedAsinsGroupByErrorReason[reason]
                                                            .slice(0, 20)
                                                            .join(', ')
                                                    if (
                                                        failedAsinsGroupByErrorReason[reason]
                                                            .length > 20
                                                    ) {
                                                        firstTwentyFailedAsins +=
                                                            ' and ' +
                                                            (
                                                                failedAsinsGroupByErrorReason[
                                                                    reason
                                                                ].length - 20
                                                            ).toString() +
                                                            ' more'
                                                    }

                                                    failedByGroup.push(
                                                        <li className="ml-2">
                                                            {reason == 'adEligibilityError'
                                                                ? popoverReason(reason)
                                                                : reason}
                                                            : {firstTwentyFailedAsins}
                                                        </li>,
                                                    )
                                                },
                                            )}
                                            {failedByGroup}
                                        </ul>
                                    </>
                                )
                            }

                            result = (
                                <>
                                    {success}
                                    <br />
                                    {failed}
                                </>
                            )
                            return result
                        } else {
                            // this is the old flow, before we introduced success attribute
                            const output = this.meta.map((item) => item.asin)
                            const firstTwentyAsins = output.slice(0, 20)
                            let result =
                                'Created Product Ads with asins: ' + firstTwentyAsins.join(', ')

                            if (output.length > 20) {
                                result =
                                    result + ' and ' + (output.length - 20).toString() + ' more'
                            }
                            return result
                        }
                    }
                    return 'Not supported iterable object'
                } else {
                    console.error('Could not resolve event for', this, this.meta)
                }
            default:
                console.warn('Unresolved event AuditLogItem for', this)
                return 'TODO'
        }
    }

    _getFormatter(attribute: string): ValueFormatter {
        switch (this.target_type_id) {
            case EventParentTypes.Recipe:
                switch (attribute) {
                    case 'enabled':
                        return (v) => (v ? 'enabled' : 'disabled')
                }
                break
            case EventParentTypes.Promotion:
                switch (attribute) {
                    case 'promotions:enabled':
                    case 'enabled':
                        return (v) => (v ? 'enabled' : 'disabled')
                }
        }

        switch (attribute) {
            case 'bid':
            case 'default bid':
            case 'daily budget':
                return cents
            case 'target acos':
                return (v) => percentage(v / 100)
            case 'smart bid':
                return (v) => (v ? 'enabled' : 'disabled')
            case 'premium bid adjustment':
                return (v) => (v ? 'true' : 'false')
            case 'state':
            case 'start date':
            case 'end date':
            case 'name':
            case 'portfolio':
            default:
                return (v) => v
        }
    }

    _resolveTargetExpressionLabel(kind: string, value: string): string {
        switch (kind) {
            case 'asinSameAs':
                return `ASIN = ${value}`
            case 'queryBroadRelMatches':
                return 'Loose match'
            case 'queryHighRelMatches':
                return 'Close match'
            case 'asinSubstituteRelated':
                return 'Substitutes'
            case 'asinAccessoryRelated':
                return 'Complements'
            case 'asinPriceGreaterThan':
                return `Price > ${value}`
            case 'asinPriceLessThan':
                return `Price < ${value}`
            case 'asinPriceBetween':
                return `Price between ${value}`
            case 'asinReviewRatingBetween':
                return `Review between ${value}`
            case 'asinReviewRatingLessThan':
                return `Review < ${value}`
            case 'asinReviewRatingGreaterThan':
                return `Review > ${value}`
            case 'asinIsPrimeShippingEligible':
                return `Prime shipping = ${value}`
            case 'asinAgeRangeSameAs':
                return `Age Range = ${value}`
            default:
                return value
        }
    }
}

const popoverReason = (reason: string) => {
    return (
        <Popover
            popoverClassName={Classes.POPOVER2_CONTENT_SIZING}
            position={'top'}
            interactionKind="hover"
            content={'These ASINs are not eligible for advertising according to Amazon'}
        >
            <span className="cursor-pointer border-b-1 border-black border-dashed">{reason}</span>
        </Popover>
    )
}
